import { useState, useEffect } from "react";
import { CalendarDays, UtensilsCrossed, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { STATUS_STYLES, STATUS_LABELS } from "@/lib/quote";

const WEEK_STATUSES = ["approvato", "acconto_ricevuto", "confermato"];
const DAYS = ["Dom", "Lun", "Mar", "Mer", "Gio", "Ven", "Sab"];
const pad = (n) => String(n).padStart(2, "0");

export const WeekAhead = ({ quotes, onSelect }) => {
  const [reservations, setReservations] = useState([]);

  const t = new Date();
  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(t.getFullYear(), t.getMonth(), t.getDate() + i);
    return { ds: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`, label: `${DAYS[d.getDay()]} ${d.getDate()}`, isToday: i === 0 };
  });
  const from = days[0].ds, to = days[6].ds;

  useEffect(() => {
    api.get("/admin/reservations", { params: { from_date: from, to_date: to } }).then((r) => setReservations(r.data)).catch(() => {});
  }, [from, to]);

  const active = days
    .map((d) => ({
      ...d,
      events: quotes.filter((q) => q.event.date === d.ds && WEEK_STATUSES.includes(q.status)),
      res: reservations.filter((r) => r.date === d.ds),
    }))
    .filter((d) => d.events.length || d.res.length);

  if (!active.length) return null;

  return (
    <div data-testid="week-ahead" className="mb-5">
      <h3 className="text-xs font-bold uppercase tracking-widest text-[#C85A32] mb-2 flex items-center gap-1.5"><CalendarDays className="w-3.5 h-3.5" /> Prossimi 7 giorni</h3>
      <div className="flex gap-2 overflow-x-auto pb-2">
        {active.map((d) => (
          <div key={d.ds} data-testid={`week-day-${d.ds}`}
            className={`min-w-[210px] max-w-[260px] shrink-0 rounded-xl border p-3 ${d.isToday ? "border-[#C85A32] bg-[#C85A32]/10" : "border-slate-700 bg-[#1E293B]"}`}>
            <div className={`text-[11px] font-bold uppercase tracking-wider mb-1.5 ${d.isToday ? "text-[#C85A32]" : "text-slate-400"}`}>{d.isToday ? "Oggi" : d.label}</div>
            {d.events.map((q) => {
              const nShifts = (q.shifts || []).length;
              return (
                <button key={q.id} data-testid={`week-event-${q.id}`} onClick={() => onSelect(q)}
                  className="w-full text-left rounded-lg border border-slate-600 hover:border-[#C85A32] p-2 mb-1.5 transition-colors">
                  <div className="text-sm font-semibold truncate">{q.event.time_start} · {q.event.type}</div>
                  <div className="text-xs text-slate-400 truncate">{q.contact.name} · {q.guests.adults + (q.guests.teens || 0) + q.guests.children} ospiti</div>
                  <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${STATUS_STYLES[q.status]}`}>{STATUS_LABELS[q.status]}</span>
                    {nShifts > 0 ? (
                      <span className="text-[10px] text-emerald-400">{nShifts} turni</span>
                    ) : (
                      <span className="text-[10px] text-amber-400 flex items-center gap-0.5"><AlertTriangle className="w-3 h-3" /> turni da assegnare</span>
                    )}
                  </div>
                </button>
              );
            })}
            {d.res.length > 0 && (
              <div data-testid={`week-res-${d.ds}`} className="text-xs text-sky-300 flex items-start gap-1.5 mt-1">
                <UtensilsCrossed className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span>{d.res.length} tavol{d.res.length === 1 ? "o" : "i"} · {d.res.reduce((s, r) => s + r.people, 0)} coperti
                  <span className="block text-slate-400">{d.res.slice(0, 3).map((r) => `${r.time || "—"} ${r.name} x${r.people}`).join(" · ")}{d.res.length > 3 ? "…" : ""}</span>
                </span>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
